import { makeUri, configure as configure$1 } from './makeUri.js';
import { DEFAULT_ERROR_CODE_PROP, getErrorCodeFromData, DEFAULT_ERROR_MSG_PROP, getErrorMsgFromData, HTTPServerError, HTTPBizError, HTTPClientError } from './errors.js';
import { Interceptor } from './interceptor.js';
import { mapStatusCodeFromLegacyResponse } from './legacyCode.js';
import { DEFAULT_TRACE_HOST_REG, TRACE_HEADER_NAME, generateTraceId } from './trace.js';
import httpStatusCode from './httpStatusCode.js';
import { AUTHORIZATION, getToken } from './auth.js';

const httpMethods = ["post", "put", "patch", "get", "delete", "head", "options"];
const httpGetMethods = ["get", "delete", "head"];
let defaultConfig;
function configure(config) {
  if (config.baseURL) {
    configure$1({ baseURL: config.baseURL });
  }
  defaultConfig = config;
}
function processSend(send, originalConfig = {}, interceptors) {
  const config = originalConfig;
  let response = null;
  const requestHandler = {
    fulfilled: (inChainConfig) => {
      if (inChainConfig !== config) {
        console.warn("[Http Warning] config reference has been changed during dispatch interceptor");
      }
      const sendPromise = send(inChainConfig);
      if (!sendPromise || !sendPromise.then) {
        throw new Error("[Http Exception] send must return a promise object");
      }
      return sendPromise;
    }
  };
  const responseStatusCodeHandler = {
    name: "responseStatusCodeHandler",
    fulfilled: (res) => {
      if (res.status === httpStatusCode.ok) {
        res.status = mapStatusCodeFromLegacyResponse(res.data);
      }
      if (Object.prototype.toString.call(res.data) === "[object Object]") {
        res.data[DEFAULT_ERROR_CODE_PROP] = getErrorCodeFromData(res.data);
        res.data[DEFAULT_ERROR_MSG_PROP] = getErrorMsgFromData(res.data);
      }
      response = res;
      if (res.status < httpStatusCode.ok || res.status >= httpStatusCode.multipleChoices) {
        throw new HTTPServerError(res.status, res.data);
      }
      if (res.data[DEFAULT_ERROR_CODE_PROP] !== 200) {
        throw new HTTPBizError(res.status, res.data);
      }
      return res.data;
    },
    rejected: (err) => {
      const msg = err.code === "ECONNABORTED" ? "\u8BF7\u6C42\u8D85\u65F6\u3002" : err.message;
      const code = Number(err.code);
      throw new HTTPClientError(code > 0 ? code : httpStatusCode.requestTimeout, msg);
    }
  };
  const responseDataHandler = {
    fulfilled: (resData, res) => {
      if (resData.success !== void 0 && Boolean(resData.success) === false) {
        throw new HTTPBizError(res.status, resData);
      }
      return resData.data;
    }
  };
  const chain = [
    ...interceptors.request.queue,
    requestHandler,
    responseStatusCodeHandler,
    ...interceptors.response.queue,
    responseDataHandler,
    ...interceptors.result.queue
  ];
  let promise = Promise.resolve(config);
  const wrapFulfilled = (handler) => (payload) => handler ? handler(payload, response, config) : payload;
  const wrapRejected = (handler) => (ex) => {
    if (handler) {
      return handler(ex, response, config);
    }
    throw ex;
  };
  while (chain.length) {
    const task = chain.shift();
    promise = promise.then(wrapFulfilled(task == null ? void 0 : task.fulfilled), wrapRejected(task == null ? void 0 : task.rejected));
  }
  return promise;
}
const sendFactory = (method, request, interceptors, withData) => (uri, payload, params) => {
  if (!uri) {
    return Promise.reject(new Error("[Http Exception] uri is empty"));
  }
  let config = { ...defaultConfig };
  if (withData) {
    config = {
      ...config,
      ...params,
      data: payload
    };
  } else {
    config = {
      ...config,
      ...payload,
      ...params
    };
  }
  config.method = method;
  config.url = uri;
  if (!config.headers) {
    config.headers = {};
  }
  if (uri.indexOf("/") === 0 || DEFAULT_TRACE_HOST_REG.test(uri)) {
    config.headers[TRACE_HEADER_NAME] = config.headers[TRACE_HEADER_NAME] || generateTraceId();
    config.headers[AUTHORIZATION] = getToken();
  }
  return processSend(request, config, interceptors);
};
function factory(request) {
  const http = {};
  const interceptors = {
    request: new Interceptor(),
    // 请求发送前
    response: new Interceptor(),
    // 响应返回但未被处理
    result: new Interceptor()
    // 响应被处理后
  };
  httpMethods.forEach((method) => {
    http[method] = sendFactory(method, request, interceptors, !httpGetMethods.includes(method));
  });
  http.del = http.delete;
  http.makeUri = makeUri;
  http.configure = configure;
  http.interceptors = interceptors;
  return http;
}

export { factory as default };
